import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook for connecting to Inkbird IBT-2X (iBBQ) Bluetooth thermometer via Web Bluetooth API.
 *
 * iBBQ Protocol (documented at https://gist.github.com/uucidl/b9c60b6d36d8080d085a8e3310621d64):
 *
 * Service: 0xFFF0
 * Characteristics:
 *   0xFFF1 - SettingsResult (notify) — receives responses to settings commands
 *   0xFFF2 - AccountAndVerify (write) — authentication/login
 *   0xFFF3 - HistoryData (notify)
 *   0xFFF4 - RealtimeData (notify) — probe temperature readings
 *   0xFFF5 - SettingsData (write) — send control commands
 *
 * Connection sequence:
 *   1. Connect GATT
 *   2. Write CredentialsMessage to 0xFFF2
 *   3. Subscribe to notifications on 0xFFF4
 *   4. Write RealtimeDataEnableMessage to 0xFFF5
 *   5. Temperature arrives on 0xFFF4 as int16 LE per probe, value / 10.0 = °C
 */

const IBBQ_SERVICE = 0xFFF0;
const CHAR_SETTINGS_RESULT = 0xFFF1;
const CHAR_ACCOUNT_VERIFY = 0xFFF2;
const CHAR_REALTIME_DATA = 0xFFF4;
const CHAR_SETTINGS_DATA = 0xFFF5;

// Hardcoded credentials for iBBQ devices (standard across all iBBQ thermometers)
const CREDENTIALS_MSG = new Uint8Array([
    0x21, 0x07, 0x06, 0x05, 0x04, 0x03, 0x02, 0x01,
    0xb8, 0x22, 0x00, 0x00, 0x00, 0x00, 0x00
]);

const REALTIME_DATA_ENABLE_MSG = new Uint8Array([
    0x0B, 0x01, 0x00, 0x00, 0x00, 0x00
]);

const UNITS_CELSIUS_MSG = new Uint8Array([
    0x02, 0x00, 0x00, 0x00, 0x00, 0x00
]);

export function useInkbirdThermometer() {
    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [deviceName, setDeviceName] = useState(null);
    const [temperature, setTemperature] = useState(null);
    const [probe1, setProbe1] = useState(null);
    const [probe2, setProbe2] = useState(null);
    const [bleSupported, setBleSupported] = useState(false);
    const deviceRef = useRef(null);
    const realtimeCharRef = useRef(null);

    useEffect(() => {
        setBleSupported(!!navigator.bluetooth);
    }, []);

    const parseTemperature = useCallback((dataView) => {
        try {
            // iBBQ sends temperature as int16 LE per probe.
            // Value is in tenths of a degree Celsius (divide by 10).
            // Number of probes = byteLength / 2
            const numProbes = Math.floor(dataView.byteLength / 2);

            if (numProbes >= 1) {
                const raw1 = dataView.getInt16(0, true); // little-endian
                const temp1 = raw1 / 10.0;
                setProbe1(temp1);
                setTemperature(temp1);
            }

            if (numProbes >= 2) {
                const raw2 = dataView.getInt16(2, true);
                const temp2 = raw2 / 10.0;
                // IBT-2X reports 0 for disconnected probes
                if (raw2 !== 0) {
                    setProbe2(temp2);
                }
            }
        } catch {
            // Parse error — ignore malformed data
        }
    }, []);

    const handleNotification = useCallback((event) => {
        parseTemperature(event.target.value);
    }, [parseTemperature]);

    const connect = useCallback(async () => {
        if (!navigator.bluetooth) {
            return { success: false, error: 'Web Bluetooth not supported' };
        }

        setConnecting(true);
        try {
            // Step 1: Request the device
            const device = await navigator.bluetooth.requestDevice({
                filters: [
                    { namePrefix: 'iBBQ' },
                    { namePrefix: 'IBT' },
                    { namePrefix: 'Inkbird' },
                    { services: [IBBQ_SERVICE] },
                ],
                optionalServices: [IBBQ_SERVICE],
            });

            device.addEventListener('gattserverdisconnected', () => {
                setConnected(false);
                setDeviceName(null);
                setTemperature(null);
                setProbe1(null);
                setProbe2(null);
                deviceRef.current = null;
                realtimeCharRef.current = null;
            });

            // Step 2: Connect GATT server
            const server = await device.gatt.connect();
            const service = await server.getPrimaryService(IBBQ_SERVICE);

            // Step 3: Authenticate — write credentials to AccountAndVerify (0xFFF2)
            const accountChar = await service.getCharacteristic(CHAR_ACCOUNT_VERIFY);
            await accountChar.writeValue(CREDENTIALS_MSG);

            // Step 4: Subscribe to real-time data notifications on 0xFFF4
            const realtimeChar = await service.getCharacteristic(CHAR_REALTIME_DATA);
            await realtimeChar.startNotifications();
            realtimeChar.addEventListener('characteristicvaluechanged', handleNotification);

            // Step 5: Enable real-time data — write enable message to SettingsData (0xFFF5)
            const settingsChar = await service.getCharacteristic(CHAR_SETTINGS_DATA);
            await settingsChar.writeValue(REALTIME_DATA_ENABLE_MSG);

            // Step 6: Set units to Celsius
            await settingsChar.writeValue(UNITS_CELSIUS_MSG);

            deviceRef.current = device;
            realtimeCharRef.current = realtimeChar;
            setConnected(true);
            setDeviceName(device.name || 'Inkbird IBT-2X');

            return { success: true, deviceName: device.name };
        } catch (err) {
            setConnected(false);
            const msg = err?.message || String(err);
            if (msg.includes('cancel')) {
                return { success: false, error: 'Pairing cancelled' };
            }
            return { success: false, error: msg };
        } finally {
            setConnecting(false);
        }
    }, [handleNotification]);

    const disconnect = useCallback(async () => {
        try {
            if (realtimeCharRef.current) {
                realtimeCharRef.current.removeEventListener('characteristicvaluechanged', handleNotification);
                await realtimeCharRef.current.stopNotifications();
            }
            if (deviceRef.current?.gatt?.connected) {
                deviceRef.current.gatt.disconnect();
            }
        } catch { /* ignore */ }
        deviceRef.current = null;
        realtimeCharRef.current = null;
        setConnected(false);
        setDeviceName(null);
        setTemperature(null);
        setProbe1(null);
        setProbe2(null);
    }, [handleNotification]);

    return {
        connected,
        connecting,
        deviceName,
        temperature,
        probe1,
        probe2,
        bleSupported,
        connect,
        disconnect,
    };
}
